import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { api, fileUrl } from "@/lib/api";

const TITLE = "O naju – Matej in Kim | NajinTrenutek.eu";
const DESC = "Spoznajta Mateja in Kim, ustanovitelja NajinTrenutek.eu. Ker tudi sama načrtujeva poroko, veva, kako pomembni so dostopne cene in nepozabni spomini.";

function useSeo() {
  useEffect(() => {
    const prev = document.title;
    document.title = TITLE;
    const set = (sel, attr, key, val) => {
      let el = document.head.querySelector(sel);
      const created = !el;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      const old = el.getAttribute("content");
      el.setAttribute("content", val);
      return () => (created ? el.remove() : el.setAttribute("content", old));
    };
    const undo = [
      set('meta[name="description"]', "name", "description", DESC),
      set('meta[property="og:title"]', "property", "og:title", TITLE),
      set('meta[property="og:description"]', "property", "og:description", DESC),
    ];
    const canon = document.createElement("link");
    canon.rel = "canonical"; canon.href = `${window.location.origin}/o-naju`;
    document.head.appendChild(canon);
    document.documentElement.lang = "sl";
    return () => { document.title = prev; undo.forEach((u) => u()); canon.remove(); };
  }, []);
}

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.7 } };

const Divider = () => (
  <div className="flex items-center justify-center gap-4 my-14" aria-hidden="true">
    <span className="h-px w-16 bg-gradient-to-r from-transparent to-[#C9A27E]" />
    <Heart size={14} className="text-[#C9A27E] fill-[#E8C9B5]" />
    <span className="h-px w-16 bg-gradient-to-l from-transparent to-[#C9A27E]" />
  </div>
);

const P = ({ children }) => <p className="text-base sm:text-lg leading-relaxed text-[#4A4440]">{children}</p>;

function Photo() {
  const [url, setUrl] = useState(null);
  useEffect(() => { api.get("/public/about").then((r) => setUrl(r.data.photo_url)).catch(() => {}); }, []);
  if (!url) {
    return (
      <div className="aspect-[4/5] rounded-[2rem] border border-dashed border-[#D9BFA8] bg-[#F3ECE1]/60 grid place-items-center text-center p-8" data-testid="about-photo-placeholder">
        <div className="space-y-3">
          <Heart className="mx-auto text-[#C9A27E]" size={28} />
          <p className="font-display text-2xl text-[#6C6560]">Matej in Kim</p>
          <p className="text-xs text-[#968E87]">Najina fotografija bo kmalu tukaj.</p>
        </div>
      </div>
    );
  }
  return <img src={fileUrl(url)} alt="Matej in Kim, ustanovitelja NajinTrenutek.eu" className="aspect-[4/5] w-full object-cover rounded-[2rem] shadow-[0_30px_60px_-30px_rgba(120,80,50,0.35)]" data-testid="about-photo" />;
}

export default function About() {
  useSeo();
  return (
    <div className="bg-[#FAF7F2] min-h-screen" data-testid="about-page">
      <SiteHeader />
      <main>
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-24 max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <motion.p {...fade} className="eyebrow">O naju</motion.p>
          <motion.h1 {...fade} className="font-display text-4xl sm:text-5xl lg:text-6xl font-light mt-4 leading-tight" data-testid="about-heading">
            Najina zgodba. Vajini spomini. <span className="text-[#C9A27E]">🤍</span>
          </motion.h1>
          <motion.p {...fade} className="mt-6 text-base md:text-lg text-[#6C6560] max-w-2xl mx-auto" data-testid="about-subtitle">
            Za NajinTrenutek stojiva Matej in Kim. Dva človeka, ki tako kot vidva sanjava o svojem popolnem poročnem dnevu.
          </motion.p>
        </section>

        <section className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-start">
          <motion.div {...fade} className="lg:sticky lg:top-28"><Photo /></motion.div>
          <motion.article {...fade} className="space-y-6" data-testid="about-story">
            <h2 className="font-display text-3xl sm:text-4xl">Živjo, sva Matej in Kim! 🤍</h2>
            <P>Tudi midva načrtujeva svojo poroko. In če sva se v tem času česa naučila, je to, da je organizacija poroke lahko čudovita, a hkrati tudi precej draga.</P>
            <P>Ko začneš seštevati stroške lokacije, hrane, glasbe, fotografa in vseh drobnih podrobnosti, hitro ugotoviš, kako pomemben je vsak evro.</P>
            <P><b className="font-medium text-[#231F20]">Prav zato sva ustvarila NajinTrenutek.eu.</b></P>
            <P>Želela sva ustvariti nekaj lepega, preprostega in predvsem cenovno dostopnega. Nekaj, kar bi si tudi sama želela imeti na svoji poroki.</P>
            <blockquote className="border-l-2 border-[#C9A27E] pl-6 py-2 font-display italic text-xl sm:text-2xl text-[#4A4440]">
              Verjameva, da najlepši spomini niso vedno tisti, ki jih ujame profesionalni fotograf. Včasih so to spontani nasmehi, nepričakovani objemi, zabavni trenutki na plesišču in fotografije, ki jih posnamejo ljudje, ki jih imava najraje.
            </blockquote>
            <P>Z NajinTrenutek lahko prav vsak svat prispeva svoj pogled na vajin poseben dan. Vse fotografije, videi in sporočila pa se zberejo na enem mestu.</P>
          </motion.article>
        </section>

        <Divider />

        <section className="bg-[#F3ECE1]/60 py-16 sm:py-24">
          <motion.div {...fade} className="max-w-3xl mx-auto px-5 sm:px-8 space-y-6" data-testid="about-prices">
            <h2 className="font-display text-3xl sm:text-4xl">Zakaj tako dostopne cene?</h2>
            <P>Ker iz prve roke veva, kako hitro se stroški poroke seštevajo.</P>
            <P>Ne želiva, da bi bilo zbiranje poročnih spominov še en velik strošek na vajinem seznamu.</P>
            <P>Zato sva oblikovala pakete, ki se začnejo že pri 20 €, in omogočila, da izbereta samo tisto, kar zares potrebujeta.</P>
            <P>Najin cilj ni ustvariti najdražje poročne storitve, ampak takšno, ki bo dostopna čim več parom.</P>
            <p className="font-display italic text-2xl text-[#B08463]">Ker si vsak par zasluži čudovite spomine, ne glede na poročni proračun.</p>
          </motion.div>
        </section>

        <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <motion.div {...fade} className="space-y-6" data-testid="about-from-us">
            <h2 className="font-display text-3xl sm:text-4xl">Od naju za vaju. ❤️</h2>
            <P>NajinTrenutek ni nastal samo kot poslovna ideja.</P>
            <P>Nastal je iz najine lastne izkušnje, priprav na poroko in želje, da bi drugim parom olajšala vsaj en majhen del njihovega velikega dne.</P>
            <P>Hvala, ker nama zaupata del svoje zgodbe.</P>
            <P>Želiva vama nepozabno poroko, polno ljubezni, smeha in trenutkov, ki jih bosta z veseljem podoživljala še mnogo let.</P>
            <div className="pt-6">
              <p className="text-[#6C6560]">Z ljubeznijo,</p>
              <p className="font-script text-5xl sm:text-6xl text-[#B08463] mt-2" data-testid="about-signature">Matej in Kim 🤍</p>
            </div>
          </motion.div>
        </section>

        <section className="px-5 sm:px-8 pb-24">
          <motion.div {...fade} className="max-w-4xl mx-auto rounded-[2rem] bg-[#231F20] text-[#FAF7F2] text-center px-6 py-16 sm:px-16 relative overflow-hidden" data-testid="about-cta">
            <Heart className="absolute -top-6 -right-6 text-[#C9A27E]/15 fill-[#C9A27E]/10" size={160} aria-hidden="true" />
            <h2 className="font-display text-3xl sm:text-4xl font-light relative">Tudi vajina zgodba si zasluži, da ostane za vedno.</h2>
            <p className="mt-5 text-[#E9DFD3] max-w-xl mx-auto relative">Naj bodo vsi nasmehi, objemi in spontani trenutki vajinega poročnega dne zbrani na enem mestu. Preprosto, osebno in dostopno.</p>
            <a href="/#cenik" className="btn-gold inline-block px-8 py-4 mt-8 relative" data-testid="about-cta-pricing">Oglejta si najine pakete</a>
            <p className="mt-4 text-sm text-[#C9A27E] relative">Paketi že od 20 €.</p>
            <p className="mt-6 text-xs text-[#968E87] relative">Preberita tudi, <Link to="/#kako-deluje" className="underline">kako deluje</Link>, ali si oglejta <Link to="/poroka/jan-in-mateja-demo" className="underline">demo poroko</Link>.</p>
          </motion.div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
